import { inject, Injectable } from '@angular/core';
import { NotificationsApiService } from '@common-services/api/notifications-api.service';
import { notificationConfigFactory } from '@dice/app/store/notifications/notification-stomp-config';
import { NotificationsActions } from '@dice/app/store/notifications/notifications.actions';
import { Environment } from '@dice/app/types/env.type';
import { ProfileNotification } from '@dice/app/types/notification.types';
import { NotificationsStateModel } from '@dice/app/types/store.type';
import { ENV } from '@dice/config.tokens';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { RxStomp, RxStompConfig, RxStompState } from '@stomp/rx-stomp';
import { cloneDeep as _cloneDeep, has as _has } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';

const DEFAULTS: NotificationsStateModel = {
  user: {
    notifications: [],
  },
  notificationId: '',
};

const stomp: RxStomp = new RxStomp();
let subscription: Subscription = Subscription.EMPTY;

@State<NotificationsStateModel>({
  name: 'notificationsModule',
  defaults: DEFAULTS,
})
@Injectable()
export class NotificationsState {
  private notificationsService = inject(NotificationsApiService);
  private env = inject<Environment>(ENV);
  private cookieService = inject(CookieService);

  private static readonly MAX_NUMBER_OF_NOTIFICATIONS_IN_FLY_OUT: number = 5;

  @Action(NotificationsActions.InitUserNotifications)
  initUserNotifications(
    ctx: StateContext<NotificationsStateModel>,
    action: NotificationsActions.InitUserNotifications,
  ): void {
    this.configureStomp();

    subscription.unsubscribe();
    subscription = this.notificationsService
      .watchUserNotification(action, stomp)
      .subscribe((response: ProfileNotification) => {
        ctx.dispatch(new NotificationsActions.AddNewUserNotification(response));
      });

    ctx.patchState({
      user: {
        notifications: [],
      },
    });
  }

  @Action(NotificationsActions.AddNewUserNotification)
  addNewUserNotification(
    ctx: StateContext<NotificationsStateModel>,
    action: NotificationsActions.AddNewUserNotification,
  ): void {
    const state: NotificationsStateModel = ctx.getState();
    const { user } = state;
    ctx.patchState({
      user: {
        ...user,
        notifications: [action.notification, ...user.notifications],
      },
    });
  }

  @Action(NotificationsActions.DeleteUserNotification)
  deleteNotification(
    ctx: StateContext<NotificationsStateModel>,
    action: NotificationsActions.DeleteUserNotification,
  ): void {
    const state: NotificationsStateModel = ctx.getState();
    if (_has(state, 'user.notifications') && state.user.notifications.length > 0) {
      const notifications: ProfileNotification[] = _cloneDeep(state.user.notifications);
      const index: number = notifications.findIndex((notification: ProfileNotification) => {
        return notification.notificationId === action.notification.notificationId;
      });
      if (index > -1) {
        notifications.splice(index, 1);
        ctx.patchState({
          user: {
            ...state.user,
            notifications,
          },
        });
      }
    }
  }

  @Action(NotificationsActions.DeleteAllNotifications)
  deleteAllUserNotifications(
    ctx: StateContext<NotificationsStateModel>,
    action: NotificationsActions.DeleteAllNotifications,
  ): void {
    const state: NotificationsStateModel = ctx.getState();
    ctx.patchState({
      user: {
        ...state.user,
        notifications: [],
      },
    });
  }

  @Action(NotificationsActions.SetNotificationId)
  setNotificationId(
    ctx: StateContext<NotificationsStateModel>,
    { notificationId }: NotificationsActions.SetNotificationId,
  ): void {
    ctx.patchState({
      notificationId,
    });
  }

  @Selector()
  static allUserNotifications(state: NotificationsStateModel): ProfileNotification[] {
    return !_has(state, 'user.notifications') ? [] : state.user.notifications;
  }

  @Selector()
  static newestUserNotifications(state: NotificationsStateModel): ProfileNotification[] {
    return !_has(state, 'user.notifications')
      ? []
      : state.user.notifications.slice(0, NotificationsState.MAX_NUMBER_OF_NOTIFICATIONS_IN_FLY_OUT);
  }

  @Selector()
  static notificationId(state: NotificationsStateModel): string {
    return state.notificationId;
  }

  private configureStomp(): void {
    const connectionState: RxStompState = stomp.connectionState$.getValue();
    if (connectionState !== RxStompState.CONNECTING && connectionState !== RxStompState.OPEN) {
      const config: RxStompConfig = notificationConfigFactory(this.env, this.cookieService);
      stomp.configure(config);
      stomp.activate();
    }
  }
}
